import ServiceExec from '@/common/utils/ServiceExec';
import { asFormData, isEmpty } from '@/common/utils/Util';
import store from '@/store';
import { cloneDeep } from 'lodash';
import { toastAlert } from '@/common/utils/module/ToastSupport';
import { CODE_ENV_LIST, SMILE_TYPE } from '@/common/constants/ConstCode';
import { nativeDownload } from '@/common/utils/module/NativeRequest';
import dayjs from 'dayjs';

const prefix = '/api/user/smile';
const imagePrefix = '/api/image/background/smile';

export default {
  /**
   * 스마일 행사 저장
   * @param state
   * @returns {Promise<*>}
   */
  async save({ state }) {
    const userData = store.getters['user/getUserData'];

    const payload = cloneDeep(state.form);

    console.log('저장 ', payload);
    payload.firstAccounts = payload.firstAccounts.filter(item => !item.bank?.includes('선택'));
    if (!isEmpty(payload.secondAccounts)) {
      payload.secondAccounts = payload.secondAccounts.filter(item => !item.bank?.includes('선택'));
    }

    payload.userNo = userData.userNo;

    payload.firstRelation = formatJSONData(payload.firstRelation, 'name');
    payload.secondRelation =
      payload.category === SMILE_TYPE.WEDDING.key ? formatJSONData(payload.secondRelation, 'name') : null;

    payload.firstAccounts = JSON.stringify(state.form.firstAccounts);
    payload.secondAccounts =
      payload.category === SMILE_TYPE.WEDDING.key ? JSON.stringify(state.form.secondAccounts) : null;

    payload.galleryRawFiles = payload.gallery.filter(e => !e.fileNo && e.type.includes('image/'));
    payload.galleryFileList = payload.gallery.filter(e => e.fileNo && e.storedFileName);

    if (payload.eventDateTime.length === 5) {
      // ex) "23:23" → "23:23:00"
      payload.eventDateTime = payload.eventDateTime + ':00';
    }
    payload.eventDate = `${payload.eventDate}T${payload.eventDateTime}`;

    try {
      const response = await ServiceExec.multiPost(prefix + '/save', asFormData(payload));
      return response.data;
    } catch (e) {
      throw new Error(e);
    }
  },

  async update({ state }) {
    const userData = store.getters['user/getUserData'];

    const payload = cloneDeep(state.form);

    payload.firstAccounts = payload.firstAccounts.filter(item => !item.bank?.includes('선택'));
    if (!isEmpty(payload.secondAccounts)) {
      payload.secondAccounts = payload.secondAccounts.filter(item => !item.bank?.includes('선택'));
    }

    payload.userNo = userData.userNo;
    payload.firstRelation = formatJSONData(payload.firstRelation, 'name');
    payload.secondRelation = formatJSONData(payload.secondRelation, 'name');

    payload.firstAccounts = JSON.stringify(state.form.firstAccounts);
    payload.secondAccounts = JSON.stringify(state.form.secondAccounts);

    payload.galleryRawFiles = payload.gallery.filter(e => !e.fileNo && e.type.includes('image/'));
    payload.galleryFileList = payload.gallery.filter(e => e.fileNo && e.storedFileName);

    const [hour, minute] = state.form.eventDateTime.split(':');
    payload.eventDate = state.form.eventDate + 'T' + hour + ':' + minute + ':00';

    delete payload.invitationFile;
    try {
      const response = await ServiceExec.multiPut(prefix + '/update', asFormData(payload));
      console.log('update response:', response);
      return response.status === ServiceExec.status.OK ? response.data : null;
    } catch (e) {
      throw new Error(e);
    }
  },

  async fetchRecentEvents({ state }) {
    const userData = store.getters['user/getUserData'];

    try {
      const response = await ServiceExec.get(prefix + `/recent/${userData.userNo}`);

      if (response.status !== ServiceExec.status.OK) {
        await toastAlert('error', 'Failed to fetch recent events.');
      }

      return response.data;
    } catch (e) {
      throw new Error(e);
    }
  },

  async fetchDetail({ state, commit }, payload) {
    const response = await ServiceExec.get(prefix + `/${payload.smileNo}`);
    if (response?.status !== ServiceExec.status.OK) {
      await toastAlert('error', 'Failed to fetch event details.');
      return;
    }
    commit('setDetail', response?.data);
    return response?.data;
  },

  async fetchPlaceList({ state, commit }, payload) {
    const response = await ServiceExec.get(prefix + `/place/list/${payload.smileNo}`);

    console.log('fetchPlaceList response:', response);
    if (response.status !== ServiceExec.status.OK) {
      await toastAlert('error', 'Failed to fetch place list.');
      return;
    }

    commit('setPlaceList', response.data ?? []);
  },

  async remove({ state, commit, getters }, smileNo) {
    const response = await ServiceExec.delete(prefix + `/${smileNo}`);

    if (response?.status !== ServiceExec.status.OK && response?.status !== ServiceExec.status.NO_CONTENT) {
      toastAlert('error', 'Failed to remove event.');
      return;
    }

    // Remove from the list
    let list = getters['getList'];
    commit(
      'mergeList',
      list.filter(e => e.smileNo !== smileNo)
    );

    // Reset detail
    commit('setDetail', {});

    return true;
  },

  async removePlaceImage({ state, commit, getters }, payload) {
    const response = await ServiceExec.delete(prefix + `/place/remove/${payload.fileNo}`);
    console.log('remove response:', response);

    let list = getters['getPlaceList'];
    // placeList.value.splice(index, 1);
    commit(
      'setPlaceList',
      list.filter(e => e.fileNo !== payload.fileNo)
    );

    return response.status === ServiceExec.status.OK;
  },

  async getList({ state, commit, getters }, payload) {
    if (payload.nextId == -1) return false;
    const response = await ServiceExec.get(prefix + `/list`, {
      ...payload
    });

    if (response.status !== ServiceExec.status.OK) {
      console.error(response);
      return;
    }

    commit('setNextId', response.data.pageInfo?.nextId);
    commit('setList', response.data.list ?? []);
  },

  async getV2List({ state, commit, getters }, payload) {
    if (payload.nextId == -1) return false;
    const response = await ServiceExec.get(prefix + `/v2/list`, {
      ...payload
    });

    if (response.status !== ServiceExec.status.OK) {
      console.error(response);
      return;
    }

    commit('setNextId', response.data.pageInfo?.nextId);
    commit('setList', response.data.list ?? []);
  },

  async getCommentList({ state, commit, getters }, payload) {
    let condition = getters['getPagingCondition'];

    if (condition.page > condition.pageInfo.totalPage) return false;

    const response = await ServiceExec.get(prefix + `/comment/list`, {
      ...payload,
      page: condition.page
    });

    if (response.status !== ServiceExec.status.OK) {
      await toastAlert('error', 'Failed to fetch comments.');
      return;
    }

    commit('setPage', condition.page + 1);
    commit('setPageInfo', response.data.pageInfo);
    commit('setCommentList', response.data.list ?? []);
  },

  async sendComment({ state, commit }, payload) {
    if (payload.emoticonNo) delete payload.comment;
    const { status, data } = await ServiceExec.post(prefix + `/comment/send`, {
      ...payload
    });

    if (status !== ServiceExec.status.OK) {
      await toastAlert('error', '축하 메세지 전송에 실패했습니다.');
      return;
    }

    commit('appendComment', data);
  },

  async removeComment({ state, commit }, commentNo) {
    const { status } = await ServiceExec.delete(prefix + `/comment/remove/${commentNo}`);

    if (status !== ServiceExec.status.OK) {
      await toastAlert('error', '축하 메세지 삭제에 실패했습니다.');
      return false;
    }
  },

  async savePlace({ state, commit }, payload) {
    const response = await ServiceExec.multiPost(prefix + '/place/save', asFormData(payload));

    if (response.status !== ServiceExec.status.OK) {
      await toastAlert('error', '이미지 등록에 실패했습니다.');
      return false;
    }

    return true;
  },

  /*
   코멘트 엑셀 파일 다운로드
   */
  async excelDownload({ state }, payload) {
    const response = await ServiceExec.get(prefix + `/excel/download/` + payload.smileNo);
    if (response.status !== ServiceExec.status.OK) {
      return false;
    }

    const today = dayjs().format('_YYYYMMDD');
    nativeDownload(
      CODE_ENV_LIST.DYNAMIC_DOMAIN + '/excel/download/' + payload.smileNo,
      `스마일_축하정보_리스트_${today}.xlsx`
    );

    return true;
  },

  async getBackground({ commit, getters, state }, category) {
    const userData = store.getters['user/getUserData'];
    const { data, status } = await ServiceExec.get(imagePrefix + `/${category}/${userData.userNo}`);
    if (ServiceExec.status.OK !== status) return;
    commit('setBackground', data ?? '');
  },

  /**
   * 현장사진 신고
   * @param payload smilNo, fileNo, userNo, reason
   * @returns {Promise<boolean>}
   */
  async reportPlace({ state, commit }, payload) {
    payload = {
      fileNo: payload.file.fileNo,
      smileNo: payload.file.targetNo,
      reason: payload.reason,
      userNo: store.getters['user/getUserData']?.userNo // 현재 로그인된 사용자 번호 (신고자)
    };
    try {
      const response = await ServiceExec.post(prefix + '/place/report', payload);
      return response?.status === ServiceExec.status.OK;
    } catch (e) {
      console.error('Error reporting place:', e);
      return false;
    }
  }
};

const formatJSONData = (list = [], value = '') => {
  if (!Array.isArray(list) || list.length === 0) return null;
  const parsingList = list.filter(e => {
    return e[value] !== null && e[value] !== '';
  });

  if (parsingList.length === 0) return null;

  return JSON.stringify(parsingList);
};
